<template>
  <div class="oa-task-page">
    <div class="page-header-card">
      <div>
        <div class="page-title">{{ currentConfig.title }}</div>
        <div class="page-subtitle">OA 工作台 / {{ currentConfig.title }}</div>
      </div>

      <div class="page-actions">
        <button class="ghost-btn" @click="goWorkbench">返回工作台</button>
        <button v-if="currentMode === 'started'" class="primary-btn" @click="goWorkbench">发起请求</button>
      </div>
    </div>

    <div class="query-card">
      <div class="query-grid">
        <input
          v-model.trim="searchForm.processNumber"
          class="query-input"
          placeholder="请输入流程编号"
          @keyup.enter="handleQuery"
        />
        <input
          v-model.trim="searchForm.keyword"
          class="query-input"
          :placeholder="currentConfig.keywordPlaceholder"
          @keyup.enter="handleQuery"
        />
        <button class="primary-btn" @click="handleQuery">搜索</button>
        <button class="ghost-btn" @click="resetQuery">重置</button>
      </div>
    </div>

    <div class="list-card">
      <div v-if="loading" class="list-state">加载中...</div>
      <div v-else-if="!dataList.length" class="list-state empty">{{ currentConfig.emptyText }}</div>

      <div v-else class="task-list">
        <div v-for="(item, index) in dataList" :key="buildRowKey(item, index)" class="task-item">
          <div class="task-header">
            <div class="task-type">
              <span class="type-text">[{{ item.isLowCodeFlow ? 'LF' : 'DIY' }}] {{ item.processTypeName || item.processKey || '流程任务' }}</span>
              <span v-if="item.isOutSideProcess" class="out-tag">OUT</span>
            </div>

            <div class="task-header-right">
              <span class="status-tag" :class="getStatusClass(item)">{{ item.taskState || '处理中' }}</span>
              <button
                class="link-btn"
                @click.stop="openTaskDetail(item)"
              >
                {{ currentConfig.itemActionText }}
              </button>
            </div>
          </div>

          <div class="task-body">
            <div class="task-row">
              <span class="task-label">流程编号：</span>
              <span class="task-value">{{ hiddenCode(item.processNumber) }}</span>
            </div>
            <div class="task-row">
              <span class="task-label">流程描述：</span>
              <span class="task-value">{{ item.description || '暂无描述' }}</span>
            </div>
            <div class="task-row">
              <span class="task-label">创建时间：</span>
              <span class="task-value">{{ formatTime(item.createTime) }}</span>
            </div>
            <div class="task-row">
              <span class="task-label">更新时间：</span>
              <span class="task-value">{{ formatTime(item.runTime) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="total > 0" class="pagination-bar">
        <div class="pagination-total">共 {{ total }} 条记录</div>
        <div class="pagination-actions">
          <button class="ghost-btn small-btn" :disabled="pageDto.page <= 1" @click="changePage(-1)">上一页</button>
          <span class="page-info">{{ pageDto.page }} / {{ totalPages }}</span>
          <button class="ghost-btn small-btn" :disabled="pageDto.page >= totalPages" @click="changePage(1)">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchTaskList } from '../api/oa';

const MODE_CONFIG_MAP = {
  todo: {
    title: '待办任务',
    itemActionText: '去审批',
    keywordPlaceholder: '请输入流程名称',
    emptyText: '暂无待办任务',
  },
  done: {
    title: '已办任务',
    itemActionText: '查看详情',
    keywordPlaceholder: '请输入流程名称',
    emptyText: '暂无已办任务',
  },
  started: {
    title: '我的发起',
    itemActionText: '查看详情',
    keywordPlaceholder: '请输入流程名称',
    emptyText: '暂无发起记录',
  },
  copy: {
    title: '抄送到我',
    itemActionText: '查看详情',
    keywordPlaceholder: '请输入流程名称',
    emptyText: '暂无抄送记录',
  },
};

export default {
  name: 'OATaskList',
  data() {
    return {
      currentMode: 'todo',
      loading: false,
      dataList: [],
      total: 0,
      pageDto: {
        page: 1,
        pageSize: 10,
      },
      searchForm: {
        processNumber: '',
        keyword: '',
      },
    };
  },
  computed: {
    currentConfig() {
      return MODE_CONFIG_MAP[this.currentMode] || MODE_CONFIG_MAP.todo;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageDto.pageSize));
    },
  },
  created() {
    this.syncMode();
    this.loadList();
  },
  watch: {
    '$route.query.mode'() {
      this.syncMode();
      this.pageDto.page = 1;
      this.loadList();
    }
  },
  methods: {
    syncMode() {
      const mode = this.$route.query.mode;
      this.currentMode = MODE_CONFIG_MAP[mode] ? mode : 'todo';
    },
    buildRowKey(item, index) {
      return item.taskId || item.processNumber || `${this.currentMode}-${index}`;
    },
    buildTaskQuery() {
      const keyword = this.searchForm.keyword || undefined;
      return {
        processNumber: this.searchForm.processNumber || undefined,
        description: keyword,
        processTypeName: keyword,
      };
    },
    async loadList() {
      this.loading = true;
      try {
        const res = await fetchTaskList(this.currentMode, this.pageDto, this.buildTaskQuery());
        if (res && res.code === 200) {
          this.dataList = res.data || [];
          this.total = (res.pagination && res.pagination.totalCount) || this.dataList.length || 0;
        } else {
          this.dataList = [];
          this.total = 0;
          this.$message.error((res && res.msg) || '获取任务列表失败');
        }
      } catch (e) {
        console.error('获取 OA 任务列表失败', e);
        this.dataList = [];
        this.total = 0;
        this.$message.error('获取任务列表失败');
      } finally {
        this.loading = false;
      }
    },
    handleQuery() {
      this.pageDto.page = 1;
      this.loadList();
    },
    resetQuery() {
      this.searchForm = {
        processNumber: '',
        keyword: '',
      };
      this.pageDto.page = 1;
      this.loadList();
    },
    changePage(offset) {
      const nextPage = this.pageDto.page + offset;
      if (nextPage < 1 || nextPage > this.totalPages) {
        return;
      }
      this.pageDto.page = nextPage;
      this.loadList();
    },
    hiddenCode(code) {
      if (!code) return '--';
      if (code.length <= 10) return code;
      return `${code.slice(0, 4)}****${code.slice(-4)}`;
    },
    formatTime(value) {
      if (!value) return '--';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hour}:${minute}`;
    },
    getStatusClass(row) {
      const taskState = String((row && row.taskState) || '');
      if (taskState.indexOf('拒绝') !== -1 || taskState.indexOf('退回') !== -1) {
        return 'status-danger';
      }
      if (taskState.indexOf('审批中') !== -1 || taskState.indexOf('处理中') !== -1) {
        return 'status-primary';
      }
      if (taskState.indexOf('通过') !== -1 || taskState.indexOf('同意') !== -1 || taskState.indexOf('已审批') !== -1) {
        return 'status-success';
      }
      if ((row && row.processState) === 6) {
        return 'status-danger';
      }
      return 'status-default';
    },
    goWorkbench() {
      this.$router.push('/home/oa');
    },
    openTaskDetail(item) {
      if (!item) {
        this.$message.warning('未获取到任务信息');
        return;
      }

      const params = {
        formCode: item.processKey,
        processNumber: item.processNumber,
        taskId: item.taskId,
        isOutSideAccess: item.isOutSideProcess,
        isLowCodeFlow: item.isLowCodeFlow,
        processTypeName: item.processTypeName || item.processKey,
        description: item.description,
        taskState: item.taskState,
        createTime: item.createTime,
        runTime: item.runTime,
        viewOnly: this.currentMode !== 'todo',
        entryType: this.currentMode,
      };

      this.$router.push({
        path: '/home/oa/approve',
        query: {
          params: encodeURIComponent(JSON.stringify(params)),
        },
      });
    },
  },
};
</script>

<style scoped>
.oa-task-page {
  height: 100%;
  padding: 16px 24px;
  background: #f5f5f7;
  box-sizing: border-box;
  overflow-y: auto;
}

.page-header-card,
.query-card,
.list-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.page-header-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 16px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #909399;
}

.page-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.query-card {
  padding: 16px 18px;
  margin-bottom: 16px;
}

.query-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 120px 120px;
  gap: 12px;
}

.query-input {
  height: 40px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #303133;
  box-sizing: border-box;
  outline: none;
}

.query-input:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
}

.primary-btn,
.ghost-btn,
.link-btn {
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.primary-btn,
.ghost-btn {
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
}

.primary-btn {
  background: #1976d2;
  color: #fff;
}

.primary-btn:hover {
  background: #1565c0;
}

.ghost-btn {
  background: #f4f4f5;
  color: #606266;
  border: 1px solid #dcdfe6;
}

.ghost-btn:hover {
  color: #1976d2;
  border-color: #1976d2;
  background: #ecf5ff;
}

.ghost-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.small-btn {
  height: 34px;
  min-width: 88px;
}

.list-card {
  padding: 18px;
}

.list-state {
  padding: 48px 16px;
  text-align: center;
  font-size: 14px;
  color: #909399;
}

.list-state.empty {
  background: #fafafa;
  border-radius: 10px;
}

.task-list {
  display: grid;
  gap: 12px;
}

.task-item {
  border: 1px solid #ebeef5;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fcfcfd;
}

.task-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.task-type {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.type-text {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.out-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 12px;
}

.task-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-primary {
  background: #ecf5ff;
  color: #1976d2;
}

.status-success {
  background: #f0f9eb;
  color: #67c23a;
}

.status-danger {
  background: #fef0f0;
  color: #f56c6c;
}

.status-default {
  background: #f4f4f5;
  color: #909399;
}

.link-btn {
  background: transparent;
  color: #1976d2;
  font-size: 13px;
  padding: 0;
}

.link-btn:hover {
  color: #1565c0;
}

.task-body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 18px;
}

.task-row {
  min-width: 0;
  font-size: 13px;
  color: #606266;
}

.task-label {
  color: #909399;
}

.task-value {
  color: #303133;
  word-break: break-all;
}

.pagination-bar {
  margin-top: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.pagination-total,
.page-info {
  font-size: 13px;
  color: #909399;
}

.pagination-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
</style>
